import React from 'react';
import * as THREE from 'three';

function Hero3D() {
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x0a0a0a, 8, 22);

    const w = container.clientWidth;
    const h = container.clientHeight;
    const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 100);
    camera.position.set(0, 0, 8.5);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    const geom = new THREE.IcosahedronGeometry(2.1, 1);
    const positions = geom.attributes.position.array.slice();
    const wireMat = new THREE.MeshBasicMaterial({ color: 0xd4ff3f, wireframe: true, transparent: true, opacity: 0.85 });
    const wireframe = new THREE.Mesh(geom, wireMat);
    scene.add(wireframe);

    const innerGeom = new THREE.IcosahedronGeometry(2.05, 1);
    const innerMat = new THREE.MeshBasicMaterial({ color: 0x0a0a0a, transparent: true, opacity: 0.9 });
    const inner = new THREE.Mesh(innerGeom, innerMat);
    scene.add(inner);

    const outerGeom = new THREE.IcosahedronGeometry(3.2, 0);
    const outerMat = new THREE.MeshBasicMaterial({ color: 0xd4ff3f, wireframe: true, transparent: true, opacity: 0.12 });
    const outer = new THREE.Mesh(outerGeom, outerMat);
    scene.add(outer);

    // Particles
    const particleCount = 1400;
    const pGeom = new THREE.BufferGeometry();
    const pPos = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      const r = 4 + Math.random() * 12;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      pPos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pPos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      pPos[i * 3 + 2] = r * Math.cos(phi) * 0.5;
    }
    pGeom.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
    const pMat = new THREE.PointsMaterial({ color: 0xffffff, size: 0.022, transparent: true, opacity: 0.55, sizeAttenuation: true, depthWrite: false });
    const particles = new THREE.Points(pGeom, pMat);
    scene.add(particles);

    const accentGeom = new THREE.BufferGeometry();
    const aCount = 220;
    const aPos = new Float32Array(aCount * 3);
    for (let i = 0; i < aCount; i++) {
      const r = 2.6 + Math.random() * 4;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      aPos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      aPos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      aPos[i * 3 + 2] = r * Math.cos(phi) * 0.4;
    }
    accentGeom.setAttribute('position', new THREE.BufferAttribute(aPos, 3));
    const accentMat = new THREE.PointsMaterial({ color: 0xd4ff3f, size: 0.045, transparent: true, opacity: 0.9, sizeAttenuation: true, depthWrite: false });
    const accentParticles = new THREE.Points(accentGeom, accentMat);
    scene.add(accentParticles);

    const torusGeom = new THREE.TorusGeometry(3.6, 0.005, 8, 200);
    const torusMat = new THREE.MeshBasicMaterial({ color: 0xd4ff3f, transparent: true, opacity: 0.4 });
    const torus = new THREE.Mesh(torusGeom, torusMat);
    torus.rotation.x = Math.PI / 2.5;
    scene.add(torus);

    const torus2Geom = new THREE.TorusGeometry(4.4, 0.003, 8, 200);
    const torus2Mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.15 });
    const torus2 = new THREE.Mesh(torus2Geom, torus2Mat);
    torus2.rotation.x = Math.PI / 4;
    torus2.rotation.y = Math.PI / 3;
    scene.add(torus2);

    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    const onMouse = (e) => {
      const rect = container.getBoundingClientRect();
      mouse.tx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      mouse.ty = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMouse);

    const onResize = () => {
      const W = container.clientWidth;
      const H = container.clientHeight;
      camera.aspect = W / H;
      camera.updateProjectionMatrix();
      renderer.setSize(W, H);
    };
    window.addEventListener('resize', onResize);

    const clock = new THREE.Clock();
    let raf;
    const animate = () => {
      const t = clock.getElapsedTime();
      mouse.x += (mouse.tx - mouse.x) * 0.04;
      mouse.y += (mouse.ty - mouse.y) * 0.04;

      const arr = geom.attributes.position.array;
      for (let i = 0; i < arr.length; i += 3) {
        const ox = positions[i];
        const oy = positions[i + 1];
        const oz = positions[i + 2];
        const len = Math.sqrt(ox * ox + oy * oy + oz * oz);
        const noise = Math.sin(t * 1.2 + ox * 1.5) * 0.04
                    + Math.sin(t * 0.8 + oy * 2.0) * 0.04
                    + Math.cos(t * 1.0 + oz * 1.8) * 0.04;
        arr[i]     = ox + (ox / len) * noise;
        arr[i + 1] = oy + (oy / len) * noise;
        arr[i + 2] = oz + (oz / len) * noise;
      }
      geom.attributes.position.needsUpdate = true;
      geom.computeVertexNormals();

      wireframe.rotation.x = t * 0.15 + mouse.y * 0.3;
      wireframe.rotation.y = t * 0.2 + mouse.x * 0.4;
      inner.rotation.copy(wireframe.rotation);

      outer.rotation.x = -t * 0.08;
      outer.rotation.y = t * 0.12;
      torus.rotation.z = t * 0.15;
      torus2.rotation.z = -t * 0.1;
      torus2.rotation.x = Math.PI / 4 + Math.sin(t * 0.3) * 0.2;

      particles.rotation.y = t * 0.02 + mouse.x * 0.15;
      particles.rotation.x = mouse.y * 0.1;
      accentParticles.rotation.y = -t * 0.05;
      accentParticles.rotation.z = t * 0.03;

      camera.position.x += (mouse.x * 0.6 - camera.position.x) * 0.04;
      camera.position.y += (-mouse.y * 0.4 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      [geom, innerGeom, outerGeom, pGeom, accentGeom, torusGeom, torus2Geom].forEach(g => g.dispose());
      [wireMat, innerMat, outerMat, pMat, accentMat, torusMat, torus2Mat].forEach(m => m.dispose());
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={containerRef} className="absolute inset-0 z-0" aria-hidden="true" />;
}

function Hero() {
  const [time, setTime] = React.useState('');
  React.useEffect(() => {
    const update = () => {
      const d = new Date();
      const opts = { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Jakarta' };
      setTime(d.toLocaleTimeString('en-GB', opts) + ' WIB');
    };
    update();
    const id = setInterval(update, 30000);
    return () => clearInterval(id);
  }, []);

  // Hero title is visible by default — see styles in index.html

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-20 px-[clamp(20px,4vw,64px)]">
      <Hero3D />
      {/* Vignette */}
      <div className="absolute inset-0 z-[1] pointer-events-none"
           style={{ background: 'radial-gradient(ellipse at center, transparent 30%, #0a0a0a 90%), linear-gradient(to bottom, transparent 70%, #0a0a0a 100%)' }} />

      <div className="relative z-[2] max-w-[1320px] mx-auto w-full">
        {/* Eyebrow */}
        <div className="font-mono text-[12px] tracking-[0.2em] text-ink-dim uppercase flex items-center gap-3 mb-7">
          <span className="w-10 h-px bg-accent"></span>
          <span>Portfolio · 2026</span>
          <span className="text-ink-muted">—</span>
          <span>Yogyakarta {time && `· ${time}`}</span>
        </div>

        {/* Title */}
        <h1 className="h-display text-[clamp(56px,11vw,168px)] leading-[0.92] tracking-tightest m-0 text-balance">
          <span className="block overflow-hidden">
            <span className="word w1">Building</span>
          </span>
          <span className="block overflow-hidden">
            <span className="word w2 text-accent">digital</span>
          </span>
          <span className="block overflow-hidden">
            <span className="word w3">experiences.</span>
          </span>
        </h1>

        {/* Meta */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-14 max-w-[880px] anim-fadein" style={{ animationDelay: '700ms' }}>
          <div>
            <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-muted mb-2.5">Who</div>
            <div className="text-[17px] text-ink leading-relaxed max-w-[38ch]">
              Adam Hanafi Syafrudin — Fullstack &amp; Mobile Developer crafting websites, AR/VR, and commerce platforms from Yogyakarta.
            </div>
          </div>
          <div>
            <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-muted mb-2.5">Currently</div>
            <div className="text-[17px] text-ink leading-relaxed max-w-[38ch]">
              Mobile Developer at Riglobe Pte. Ltd. (Singapore). Open to selective freelance and contract collaborations.
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-14 flex gap-3.5 flex-wrap anim-fadein" style={{ animationDelay: '900ms' }}>
          <a href="#work" className="group inline-flex items-center gap-2.5 px-[22px] py-3.5 rounded-full font-mono text-[13px] tracking-wider bg-accent text-black border border-accent transition-all duration-200 hover:bg-[#e6ff66] hover:shadow-[0_12px_40px_-10px_rgba(212,255,63,0.35)]">
            See selected work
            <span className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">↗</span>
          </a>
          <a href="#contact" className="group inline-flex items-center gap-2.5 px-[22px] py-3.5 rounded-full font-mono text-[13px] tracking-wider border border-borderc-strong text-ink transition-all duration-200 hover:border-ink-dim hover:bg-white/5">
            Get in touch
            <span className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">→</span>
          </a>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-7 left-1/2 -translate-x-1/2 z-[2] font-mono text-[11px] tracking-[0.2em] text-ink-muted uppercase flex flex-col items-center gap-3 anim-fadein" style={{ animationDelay: '1400ms' }}>
        <span>Scroll</span>
        <span className="w-px h-[42px] block anim-scrolline origin-top" style={{ background: 'linear-gradient(to bottom, #d4ff3f, transparent)' }}></span>
      </div>
    </section>
  );
}

export default Hero;
